import { forwardRef, useEffect } from 'react'
import { format } from 'date-fns'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts'
import type { ForkliftStats, ScenarioStats } from '@/types/forklift'
import { getScenarioLabel } from '@/common/scenarioLabel'
import { formatDuration } from '@/common/duration'
import { SCENARIO_COLORS } from '@/common/scenarioColors'
import ScenarioRunTimeline, {
  type TimelineOrientation,
} from './ScenarioRunTimeline'
import { buildForkLanes } from '@/common/forkLanes'

// A4 幅 (210mm) から左右余白 10mm ずつを引いた 190mm ≒ 718px (96dpi)
export const REPORT_WIDTH_PX = 718
const CHART_HEIGHT_PX = 230
// 日別シナリオ実行時刻を横=時刻/縦=日付で描くときの 1 ページあたりの日数。
// A4 縦のコンテンツ高さ (約 1047px) に compact の行高 7px × 日数 + 余白が収まる値
const TIMELINE_DAYS_PER_PAGE = 120

interface MetricsReportProps {
  // 表示用の機体ラベル (複数機体の合算時は「fork_a, fork_b」や「すべて (2台)」)
  forkId: string
  siteId?: string
  period: { start: string; end: string }
  stats?: ForkliftStats
  scenarioStats?: ScenarioStats
  // 描画完了 (レイアウト確定) 後に呼ばれる。PDF キャプチャの開始合図
  onRendered?: () => void
  // 日別シナリオ実行時刻の軸 (画面の切替に合わせる)。time-x は日数ぶん縦に伸びるので
  // TIMELINE_DAYS_PER_PAGE 日ごとに分けて描き、各ブロックをページ分割候補にする
  timelineOrientation?: TimelineOrientation
}

// 日数を TIMELINE_DAYS_PER_PAGE ごとの [from, to) に分ける
function timelineChunks(dayCount: number): Array<[number, number]> {
  const chunks: Array<[number, number]> = []
  for (let from = 0; from < dayCount; from += TIMELINE_DAYS_PER_PAGE) {
    chunks.push([from, Math.min(dayCount, from + TIMELINE_DAYS_PER_PAGE)])
  }
  return chunks
}

function SummaryTile({
  label,
  value,
  unit,
  color,
}: {
  label: string
  value: string | number
  unit?: string
  color: string
}) {
  return (
    <div className="border border-gray-200 rounded p-2">
      <p className="text-[10px] text-gray-500">{label}</p>
      <p className={`text-base font-bold mt-0.5 ${color}`}>
        {value}
        {unit && (
          <span className="text-[10px] font-normal text-gray-400 ml-1">
            {unit}
          </span>
        )}
      </p>
    </div>
  )
}

// メトリクス画面の数値とグラフを A4 縦に収めるレポートレイアウト。
// 画面外に描画し html2canvas でキャプチャして PDF にする前提のため、
// 幅は固定・グラフは ResponsiveContainer を使わずアニメーション無しで描く。
const MetricsReport = forwardRef<HTMLDivElement, MetricsReportProps>(
  function MetricsReport(
    {
      forkId,
      siteId,
      period,
      stats,
      scenarioStats,
      onRendered,
      timelineOrientation = 'date-x',
    },
    ref
  ) {
    useEffect(() => {
      if (!onRendered) return
      // SVG の描画が終わるのを 2 フレーム待ってから通知する
      let frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => onRendered())
      })
      return () => cancelAnimationFrame(frame)
      // マウント時に一度だけ通知する
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const scenarios = scenarioStats?.scenarios ?? []
    const scenarioLabelMap = Object.fromEntries(
      scenarios.map((s) => [s.id, getScenarioLabel(s)])
    )
    const scenarioColorMap = Object.fromEntries(
      scenarios.map((s, i) => [
        s.id,
        SCENARIO_COLORS[i % SCENARIO_COLORS.length],
      ])
    ) as Record<string, string>
    const hasRuns =
      !!scenarioStats?.daily &&
      scenarioStats.daily.some((d) => d.runs && d.runs.length > 0)
    const hasDailyScenario =
      !!scenarioStats?.daily &&
      scenarioStats.daily.length > 0 &&
      scenarios.length > 0
    const hasDailyDistance =
      !!stats?.data && stats.data.some((d) => d.distance_km > 0)
    const errorBreakdown = scenarioStats?.error_stats?.breakdown ?? []
    // 複数機体の合算時はシナリオ別集計に機体列を出し、日別グラフは機体別に積み上げる
    const showForkColumn = scenarios.some(
      (s) => s.fork_ids && s.fork_ids.length > 0
    )
    const forkLanes = buildForkLanes(scenarioStats?.fork_ids)
    const useForkBreakdown = forkLanes.length > 1 && !!scenarioStats?.fork_daily
    const hasForkDaily =
      useForkBreakdown && (scenarioStats?.fork_daily?.length ?? 0) > 0
    const dateTickInterval =
      scenarioStats?.daily && scenarioStats.daily.length > 40
        ? Math.ceil(scenarioStats.daily.length / 20)
        : 0

    return (
      <div
        ref={ref}
        style={{ width: REPORT_WIDTH_PX }}
        className="bg-white text-gray-900 text-[11px] leading-snug"
      >
        {/* ヘッダー */}
        <div className="border-b-2 border-gray-800 pb-2 mb-3">
          <h1 className="text-lg font-bold">メトリクスレポート</h1>
          <div className="flex justify-between mt-1 text-gray-600">
            <span>
              機体: <span className="font-medium text-gray-900">{forkId}</span>
              {siteId && (
                <>
                  {' '}
                  / サイト:{' '}
                  <span className="font-medium text-gray-900">{siteId}</span>
                </>
              )}
            </span>
            <span>
              期間: {period.start} 〜 {period.end}
            </span>
            <span>出力日時: {format(new Date(), 'yyyy-MM-dd HH:mm')}</span>
          </div>
        </div>

        {/* サマリー */}
        <div data-report-break className="mb-4">
          <h2 className="text-sm font-semibold mb-2">サマリー</h2>
          <div className="grid grid-cols-4 gap-2">
            <SummaryTile
              label="シナリオ実行回数"
              value={scenarioStats?.total_executions ?? 0}
              unit="回"
              color="text-primary-600"
            />
            <SummaryTile
              label="完了回数"
              value={scenarioStats?.total_completions ?? 0}
              unit="回"
              color="text-green-600"
            />
            <SummaryTile
              label="搬送回数"
              value={scenarioStats?.total_transports ?? 0}
              unit="回"
              color="text-teal-600"
            />
            <SummaryTile
              label="エラー停止回数"
              value={scenarioStats?.total_error_stops ?? 0}
              unit="回"
              color="text-orange-500"
            />
            <SummaryTile
              label="総走行距離"
              value={stats?.summary.total_distance_km ?? 0}
              unit="km"
              color="text-blue-600"
            />
            <SummaryTile
              label="シナリオ実行時間（期間）"
              value={formatDuration(scenarioStats?.total_duration_ms ?? 0)}
              color="text-indigo-600"
            />
            <SummaryTile
              label="シナリオ実行時間（いままでの累計）"
              value={formatDuration(scenarioStats?.cumulative_duration_ms ?? 0)}
              color="text-indigo-600"
            />
            {(scenarioStats?.total_pause_count ?? 0) > 0 && (
              <SummaryTile
                label="一時停止"
                value={`${scenarioStats?.total_pause_count} 回 / ${formatDuration(
                  scenarioStats?.total_pause_ms ?? 0
                )}`}
                color="text-gray-700"
              />
            )}
          </div>
        </div>

        {/* 日別シナリオ実行数 */}
        <div data-report-break className="mb-4">
          <h2 className="text-sm font-semibold mb-1">日別シナリオ実行数</h2>
          {useForkBreakdown ? (
            hasForkDaily ? (
              <BarChart
                width={REPORT_WIDTH_PX}
                height={CHART_HEIGHT_PX}
                data={scenarioStats!.fork_daily}
                margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 9 }}
                  interval={dateTickInterval}
                />
                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                <Legend wrapperStyle={{ fontSize: 9 }} iconSize={8} />
                {forkLanes.map((lane) => (
                  <Bar
                    key={lane.id}
                    dataKey={`counts.${lane.id}.executions`}
                    name={lane.label}
                    stackId="a"
                    fill={lane.color}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            ) : (
              <p className="text-gray-400 py-6 text-center">
                データがありません
              </p>
            )
          ) : hasDailyScenario ? (
            <BarChart
              width={REPORT_WIDTH_PX}
              height={CHART_HEIGHT_PX}
              data={scenarioStats!.daily}
              margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => v.slice(5)}
                tick={{ fontSize: 9 }}
                interval={dateTickInterval}
              />
              <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
              <Legend wrapperStyle={{ fontSize: 9 }} iconSize={8} />
              {scenarios.map((s, i) => (
                <Bar
                  key={s.id}
                  dataKey={`${s.id}.executions`}
                  name={scenarioLabelMap[s.id] || s.id}
                  stackId="a"
                  fill={SCENARIO_COLORS[i % SCENARIO_COLORS.length]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          ) : (
            <p className="text-gray-400 py-6 text-center">データがありません</p>
          )}
        </div>

        {/* 日別走行距離 */}
        <div data-report-break className="mb-4">
          <h2 className="text-sm font-semibold mb-1">日別走行距離 (km)</h2>
          {hasDailyDistance ? (
            <BarChart
              width={REPORT_WIDTH_PX}
              height={CHART_HEIGHT_PX}
              data={stats!.data}
              margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => v.slice(5)}
                tick={{ fontSize: 9 }}
                interval={dateTickInterval}
              />
              <YAxis tick={{ fontSize: 9 }} />
              {useForkBreakdown && (
                <Legend wrapperStyle={{ fontSize: 9 }} iconSize={8} />
              )}
              {useForkBreakdown ? (
                forkLanes.map((lane) => (
                  <Bar
                    key={lane.id}
                    dataKey={`distance_by_fork.${lane.id}`}
                    name={lane.label}
                    stackId="d"
                    fill={lane.color}
                    isAnimationActive={false}
                  />
                ))
              ) : (
                <Bar
                  dataKey="distance_km"
                  fill="#3b82f6"
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
              )}
            </BarChart>
          ) : (
            <p className="text-gray-400 py-6 text-center">データがありません</p>
          )}
        </div>

        {/* 日別シナリオ実行時刻 (稼働時間) */}
        <div data-report-break className="mb-4">
          <h2 className="text-sm font-semibold mb-1">日別シナリオ実行時刻</h2>
          {hasRuns && timelineOrientation === 'time-x' ? (
            timelineChunks(scenarioStats!.daily.length).map(
              ([from, to], i, chunks) => (
                <div key={from} data-report-break className="mb-2">
                  <ScenarioRunTimeline
                    daily={scenarioStats!.daily}
                    scenarios={scenarios}
                    scenarioLabelMap={scenarioLabelMap}
                    scenarioColorMap={scenarioColorMap}
                    lanes={useForkBreakdown ? forkLanes : undefined}
                    width={REPORT_WIDTH_PX}
                    height={40}
                    compact
                    orientation="time-x"
                    dayRange={[from, to]}
                    showSummary={i === chunks.length - 1}
                  />
                </div>
              )
            )
          ) : hasRuns ? (
            <ScenarioRunTimeline
              daily={scenarioStats!.daily}
              scenarios={scenarios}
              scenarioLabelMap={scenarioLabelMap}
              scenarioColorMap={scenarioColorMap}
              lanes={useForkBreakdown ? forkLanes : undefined}
              width={REPORT_WIDTH_PX}
              height={CHART_HEIGHT_PX}
              compact
            />
          ) : (
            <p className="text-gray-400 py-6 text-center">データがありません</p>
          )}
        </div>

        {/* シナリオ別集計 */}
        <div className="mb-4">
          <h2
            data-report-break="section"
            className="text-sm font-semibold mb-1"
          >
            シナリオ別集計
          </h2>
          {scenarios.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-[10px] text-gray-600">
                  <th className="border border-gray-200 px-2 py-1 text-left">
                    シナリオ
                  </th>
                  {showForkColumn && (
                    <th className="border border-gray-200 px-2 py-1 text-left w-28">
                      機体
                    </th>
                  )}
                  <th className="border border-gray-200 px-2 py-1 text-right w-16">
                    実行回数
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-14">
                    完了
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-14">
                    中断
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-16">
                    エラー停止
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-14">
                    完了率
                  </th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((scenario) => {
                  const completionRate =
                    scenario.executions > 0
                      ? Math.round(
                          (scenario.completions / scenario.executions) * 100
                        )
                      : 0
                  return (
                    <tr key={scenario.id} data-report-break>
                      <td className="border border-gray-200 px-2 py-1">
                        {getScenarioLabel(scenario)}
                      </td>
                      {showForkColumn && (
                        <td className="border border-gray-200 px-2 py-1 text-gray-600">
                          {scenario.fork_ids?.join(', ') ?? ''}
                        </td>
                      )}
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {scenario.executions}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-green-600">
                        {scenario.completions}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-red-500">
                        {scenario.cancellations}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-orange-500">
                        {scenario.error_stops ?? '－'}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {completionRate}%
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 py-4 text-center">
              指定期間のシナリオデータがありません
            </p>
          )}
        </div>

        {/* エラー別集計 */}
        {errorBreakdown.length > 0 && (
          <div className="mb-2">
            <div
              data-report-break="section"
              className="flex items-baseline justify-between mb-1"
            >
              <h2 className="text-sm font-semibold">エラー別集計</h2>
              <p className="text-[10px] text-gray-400">
                機体イベントジャーナル由来（対応機体のみ）
              </p>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-[10px] text-gray-600">
                  <th className="border border-gray-200 px-2 py-1 text-left w-14">
                    コード
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-left">
                    内容
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-16">
                    発生回数
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-16">
                    実行停止
                  </th>
                  <th className="border border-gray-200 px-2 py-1 text-right w-20">
                    停止時間合計
                  </th>
                </tr>
              </thead>
              <tbody>
                {errorBreakdown.map((e) => (
                  <tr key={e.code} data-report-break>
                    <td className="border border-gray-200 px-2 py-1 font-medium">
                      {e.code}
                    </td>
                    <td className="border border-gray-200 px-2 py-1">
                      {e.message || '－'}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {e.occurred}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-orange-500">
                      {e.stops}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {formatDuration(e.downtime_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }
)

export default MetricsReport
